import axios from 'axios'
import router from '@/router/index'
import { mockInterceptor } from '@tests/Mocks/routes'
import type { Filter } from '@/typings/Filter'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

class ApiRequest {
  private readonly axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    })

    if (import.meta.env.VITE_USE_API_MOCKS === 'true') {
      this.axiosInstance.interceptors.request.use(mockInterceptor)
    }

    // Add authorization header to all requests
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('authToken')
        const tokenType = localStorage.getItem('authTokenType')
        if (token && tokenType) {
          config.headers = config.headers || {}
          config.headers['Authorization'] = `${tokenType} ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Unauthorized request interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          // Redirect to login page
          // Prevent infinite loop
          if (window.location.pathname !== '/login') {
            router.push('/login').then(() => {})
          }
          // Clear token
          localStorage.removeItem('authToken')
          localStorage.removeItem('authTokenType')
          localStorage.removeItem('authUser')
        }
        return Promise.reject(error)
      },
    )
  }

  public async get<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    if (data) {
      url += this.toQueryString(data)
    }
    const response: AxiosResponse<T> = await this.axiosInstance.get<T>(url, config)
    return response.data
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.post<T>(url, data, config)
    return response.data
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.put<T>(url, data, config)
    return response.data
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.delete<T>(url, config)
    return response.data
  }

  public toQueryString(filters?: any | Filter) {
    if (!filters) {
      return ''
    }
    if (filters['period']) {
      const dates = filters.period.split(' - ')
      filters['startDate'] = dates[0]
      filters['endDate'] = dates[1]
    }
    if (filters['distributionChannel'] === null) {
      delete filters['distributionChannel']
    }
    if (filters['customerId'] === null) {
      delete filters['customerId']
    }
    if (filters['serviceLevel'] === null) {
      delete filters['serviceLevel']
    }
    return (
      '?' +
      Object.keys(filters)
        .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(filters[key]))
        .join('&')
    )
  }
}

export { ApiRequest }

// Usage example:
// const api = new ApiRequest();
// const data = await api.get<SomeType>('/some/url');
